/* Phase 68 — Brand + Model picker with searchable virtualized comboboxes.
 *
 * Replaces the old native <Select> with two SearchableCombobox instances —
 * fixes the "scroll too fast on desktop" UX and gives us:
 *   - type-to-search across 150+ brands and 1000+ models
 *   - keyboard navigation (↑/↓/Enter/Esc)
 *   - sticky search input + debounced filter
 *   - "Recent" + "Popular" sections at the top
 *   - "Andere Marke / Anderes Modell" → free-text Input fallback
 *
 * Recently picked items are remembered per scope in localStorage so the same
 * dealer picking BMW + 3er twice gets a 1-click selection the second time.
 *
 * Used by CreateListing form and the AdvancedFilters search panel.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { BRANDS } from "../lib/constants";
import { getModelsForBrand } from "../lib/brandModels";
import {
  getRecent,
  pushRecent,
  POPULAR_BRANDS_CH,
  POPULAR_MODELS_BY_BRAND,
} from "../lib/recentPicks";
import SearchableCombobox from "./SearchableCombobox";
import { Input } from "./ui/input";

const ANY_SENTINEL = "__any__";

export default function BrandModelSelect({
  brand,
  model,
  onChange,                 // (next: { brand, model }) => void
  required = false,
  allowAny = false,         // adds an "Alle Marken" / "Alle Modelle" option (search mode)
  labelBrand = "Marke",
  labelModel = "Modell",
  otherLabel = "Anderes Modell",
  anyLabel = "Alle",
  brandTestId = "form-brand",
  modelTestId = "form-model",
}) {
  const models = useMemo(() => getModelsForBrand(brand), [brand]);
  const brandKnown = !brand || models.length > 0;
  const modelKnown = !model || models.includes(model);

  // Auto-detect "other" mode (saved model isn't in current brand's list, or
  // user explicitly clicked "Andere Marke" in the brand combobox)
  const [otherBrandMode, setOtherBrandMode] = useState(false);
  const [otherModelMode, setOtherModelMode] = useState(false);

  useEffect(() => {
    if (brand && !brandKnown) setOtherBrandMode(true);
    if (brand && model && !modelKnown) setOtherModelMode(true);
    if (!brand) { setOtherBrandMode(false); setOtherModelMode(false); }
  }, [brand, model, brandKnown, modelKnown]);

  // Phase 68 — recent + popular pickers (localStorage backed)
  const [recentBrands, setRecentBrands] = useState(() => getRecent("brand"));
  const recentModels = useMemo(() => brand ? getRecent(`model:${brand}`) : [], [brand, model]);
  const popularModels = useMemo(() => {
    if (!brand) return [];
    return (POPULAR_MODELS_BY_BRAND[brand] || []).filter((m) => models.includes(m));
  }, [brand, models]);

  const handleBrandPick = useCallback((b) => {
    if (b === ANY_SENTINEL) { onChange({ brand: "", model: "" }); return; }
    pushRecent("brand", b);
    setRecentBrands(getRecent("brand"));
    onChange({ brand: b, model: "" });
    setOtherBrandMode(false);
    setOtherModelMode(false);
  }, [onChange]);

  const handleModelPick = useCallback((m) => {
    if (m === ANY_SENTINEL) { onChange({ brand, model: "" }); return; }
    if (brand) pushRecent(`model:${brand}`, m);
    onChange({ brand, model: m });
    setOtherModelMode(false);
  }, [brand, onChange]);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {/* Brand */}
      <div data-testid="brand-model-brand-wrap">
        <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-zinc-400">
          {labelBrand}{required && !allowAny ? <span className="ml-1 text-red-500">*</span> : null}
        </label>
        {otherBrandMode ? (
          <div className="flex gap-2">
            <Input
              value={brand || ""}
              onChange={(e) => onChange({ brand: e.target.value, model: "" })}
              placeholder="Eigene Marke eingeben…"
              className="border-zinc-800 bg-zinc-950 text-white"
              data-testid={`${brandTestId}-other-input`}
            />
            <button
              type="button"
              onClick={() => { setOtherBrandMode(false); onChange({ brand: "", model: "" }); }}
              className="rounded-md border border-zinc-800 px-2 text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white"
              data-testid={`${brandTestId}-back-to-list`}
            >
              ← Liste
            </button>
          </div>
        ) : (
          <SearchableCombobox
            value={brand || ""}
            onChange={handleBrandPick}
            options={BRANDS}
            recent={recentBrands.filter((b) => BRANDS.includes(b))}
            popular={POPULAR_BRANDS_CH.filter((b) => BRANDS.includes(b))}
            placeholder={allowAny ? anyLabel : "—"}
            searchPlaceholder="Marke suchen…"
            emptyMessage="Keine Marke gefunden"
            otherLabel={!allowAny ? "Andere Marke (eintippen)…" : null}
            onOther={() => { setOtherBrandMode(true); onChange({ brand: "", model: "" }); }}
            testId={brandTestId}
          />
        )}
      </div>

      {/* Model */}
      <div data-testid="brand-model-model-wrap">
        <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-zinc-400">
          {labelModel}{required && !allowAny ? <span className="ml-1 text-red-500">*</span> : null}
        </label>
        {otherModelMode ? (
          <div className="flex gap-2">
            <Input
              value={model || ""}
              onChange={(e) => onChange({ brand, model: e.target.value })}
              placeholder={otherLabel}
              className="border-zinc-800 bg-zinc-950 text-white"
              data-testid={`${modelTestId}-other-input`}
              disabled={!brand}
            />
            <button
              type="button"
              onClick={() => { setOtherModelMode(false); onChange({ brand, model: "" }); }}
              className="rounded-md border border-zinc-800 px-2 text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white"
              data-testid={`${modelTestId}-back-to-list`}
            >
              ← Liste
            </button>
          </div>
        ) : (
          <SearchableCombobox
            value={model || ""}
            onChange={handleModelPick}
            options={models}
            recent={recentModels.filter((m) => models.includes(m))}
            popular={popularModels}
            placeholder={brand ? (allowAny ? anyLabel : "—") : "Marke zuerst wählen"}
            searchPlaceholder="Modell suchen…"
            emptyMessage="Kein Modell gefunden"
            otherLabel={brand && !allowAny ? `${otherLabel} (eintippen)…` : null}
            onOther={() => setOtherModelMode(true)}
            disabled={!brand}
            testId={modelTestId}
          />
        )}
      </div>
    </div>
  );
}
